import validator from 'validator';

/**
 * Validation and Sanitization Utilities
 * Provides comprehensive input validation and XSS protection
 */

export const validationUtils = {
  // Email validation
  isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    return validator?.isEmail(email?.trim());
  },

  // Phone validation (Indian format)
  isValidPhone(phone) {
    if (!phone || typeof phone !== 'string') return false;
    const cleaned = phone?.replace(/[\s()-]/g, '');
    return /^[6-9]\d{9}$/?.test(cleaned);
  },

  // Password strength validation
  isStrongPassword(password) {
    if (!password || typeof password !== 'string') return false;
    return validator?.isStrongPassword(password, {
      minLength: 8,
      minLowercase: 1,
      minUppercase: 1,
      minNumbers: 1,
      minSymbols: 0
    });
  },

  // URL validation
  isValidURL(url) {
    if (!url || typeof url !== 'string') return false;
    return validator?.isURL(url, { require_protocol: true });
  },

  // UUID validation
  isValidUUID(uuid) {
    if (!uuid || typeof uuid !== 'string') return false;
    return validator?.isUUID(uuid);
  },

  // Sanitize HTML to prevent XSS
  sanitizeHTML(input) {
    if (!input || typeof input !== 'string') return '';
    return validator?.escape(input);
  },

  // Sanitize string (trim and escape)
  sanitizeString(input) {
    if (!input || typeof input !== 'string') return '';
    return validator?.escape(validator?.trim(input));
  },

  // Validate and sanitize name
  validateName(name) {
    if (!name || typeof name !== 'string') {
      return { valid: false, error: 'Name is required', sanitized: '' };
    }
    const sanitized = validator?.trim(name);
    if (sanitized?.length < 2) {
      return { valid: false, error: 'Name must be at least 2 characters', sanitized };
    }
    if (sanitized?.length > 100) {
      return { valid: false, error: 'Name must be less than 100 characters', sanitized };
    }
    return { valid: true, error: null, sanitized: validator?.escape(sanitized) };
  },

  // Validate date range
  isValidDateRange(startDate, endDate) {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start < end && !isNaN(start) && !isNaN(end);
  },

  // Validate future date
  isFutureDate(date) {
    if (!date) return false;
    const inputDate = new Date(date);
    const now = new Date();
    return inputDate > now && !isNaN(inputDate);
  },

  // Sanitize object (recursively sanitize all string values)
  sanitizeObject(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        sanitized[key] = validator?.escape(validator?.trim(value));
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
};

// Form validation schemas
export const formSchemas = {
  registration: {
    fullName: (value) => validationUtils?.validateName(value),
    email: (value) => {
      if (!validationUtils?.isValidEmail(value)) {
        return { valid: false, error: 'Invalid email format' };
      }
      return { valid: true, error: null, sanitized: validator?.normalizeEmail(value) };
    },
    mobile: (value) => {
      if (!validationUtils?.isValidPhone(value)) {
        return { valid: false, error: 'Invalid mobile number (10 digits starting with 6-9)' };
      }
      return { valid: true, error: null, sanitized: value?.replace(/[\s()-]/g, '') };
    },
    password: (value) => {
      if (!validationUtils?.isStrongPassword(value)) {
        return { valid: false, error: 'Password must be at least 8 characters with uppercase, lowercase, and number' };
      }
      return { valid: true, error: null, sanitized: value };
    }
  },
  
  booking: {
    date: (value) => {
      if (!validationUtils?.isFutureDate(value)) {
        return { valid: false, error: 'Please select a future date' };
      }
      return { valid: true, error: null, sanitized: value };
    },
    time: (value) => {
      if (!value || !/^([01]\d|2[0-3]):([0-5]\d)$/?.test(value)) {
        return { valid: false, error: 'Invalid time format' };
      }
      return { valid: true, error: null, sanitized: value };
    }
  }
};

export default validationUtils;